<mat-card appearance="outlined">
  <mat-card-title>IoT Edge Releases</mat-card-title>

  <div class="release-actions">
    <button mat-raised-button (click)="syncReleases()" [disabled]="syncing">
      <mat-icon>sync</mat-icon> Sync from IoT Hub
    </button>
  </div>

  <!-- Status Overview -->
  <div class="release-status" *ngIf="siteReleases">
    <mat-card appearance="outlined" class="status-card">
      <mat-card-subtitle>Current Release (running on device)</mat-card-subtitle>
      <mat-card-content>
        <span *ngIf="siteReleases.currentRelease" class="release-badge current">
          {{ siteReleases.currentRelease.version }}
        </span>
        <span *ngIf="siteReleases.currentRelease?.releaseDate" class="release-date-hint">
          {{ siteReleases.currentRelease.releaseDate | date: 'dd.MM.yyyy' }}
        </span>
        <span *ngIf="!siteReleases.currentRelease" class="release-badge none">Not detected</span>
      </mat-card-content>
    </mat-card>

    <mat-card appearance="outlined" class="status-card">
      <mat-card-subtitle>Desired Release (target)</mat-card-subtitle>
      <mat-card-content>
        <span *ngIf="siteReleases.desiredRelease" class="release-badge desired">
          {{ siteReleases.desiredRelease.version }}
        </span>
        <span *ngIf="siteReleases.desiredRelease?.releaseDate" class="release-date-hint">
          {{ siteReleases.desiredRelease.releaseDate | date: 'dd.MM.yyyy' }}
        </span>
        <span *ngIf="!siteReleases.desiredRelease" class="release-badge none">Not set</span>
      </mat-card-content>
    </mat-card>

    <mat-card appearance="outlined" class="status-card">
      <mat-card-subtitle>Latest Available</mat-card-subtitle>
      <mat-card-content>
        <span *ngIf="latestAvailableRelease" class="release-badge latest">
          {{ latestAvailableRelease.version }}
        </span>
        <span *ngIf="latestAvailableRelease?.releaseDate" class="release-date-hint">
          {{ latestAvailableRelease.releaseDate | date: 'dd.MM.yyyy' }}
        </span>
        <span *ngIf="!latestAvailableRelease" class="release-badge none">No releases</span>
        <mat-icon
          *ngIf="isLatestAvailableReady !== null"
          class="ready-icon"
          [class.ready-icon-ready]="isLatestAvailableReady === true"
          [class.ready-icon-pending]="isLatestAvailableReady === false"
          [matTooltip]="isLatestAvailableReady ? 'Ready to install on this site' : 'Not yet downloaded on this site'"
          >download</mat-icon
        >
        <span *ngIf="latestAvailableRelease && siteReleases.currentRelease && !isOnLatest" class="update-hint">
          ↑ Update available
        </span>
        <span *ngIf="isOnLatest" class="up-to-date-hint">✓ Up to date</span>
      </mat-card-content>
    </mat-card>
  </div>

  <!-- Stale On-Prem Warning Banner -->
  <div class="stale-banner" *ngIf="siteReleases && isOnPremStale">
    <mat-icon class="stale-banner-icon">warning</mat-icon>
    <span *ngIf="siteReleases.reportedContainersAt">
      On-prem health data is outdated — last report was
      {{ siteReleases.reportedContainersAt | date: 'dd.MM.yyyy HH:mm' }} UTC (more than 1 hour ago).
    </span>
    <span *ngIf="!siteReleases.reportedContainersAt">
      On-prem gateway has never reported container status for this site.
    </span>
  </div>

  <!-- Deployment Health Section -->
  <div class="deployment-health-section" *ngIf="siteReleases" [ngClass]="deploymentStatusClass">
    <div class="health-header">
      <mat-icon class="health-icon">{{ deploymentStatusIcon }}</mat-icon>
      <span class="health-title">IoT Edge Reported Versions: {{ deploymentStatusLabel }}</span>
      <span *ngIf="siteReleases.deploymentHealthCheckedAt" class="health-checked-at">
        Last checked {{ siteReleases.deploymentHealthCheckedAt | date: 'dd.MM.yyyy HH:mm' }} UTC
      </span>
      <span *ngIf="isIotEdgeStale" class="stale-warning">
        <mat-icon class="stale-icon">schedule</mat-icon> Outdated (no report in 1+ hour)
      </span>
    </div>

    <!-- Detailed explanation when not healthy -->
    <div *ngIf="normalizedStatus === 'healthy' && isVersionPending" class="health-detail">
      <p>
        IoT Edge reports the deployment as healthy, but the device is still running
        <strong>{{ siteReleases.currentRelease.version }}</strong> while the desired release is
        <strong>{{ siteReleases.desiredRelease.version }}</strong>.
        The update may still be downloading or in the process of being applied.
      </p>
    </div>

    <div *ngIf="normalizedStatus === 'failure'" class="health-detail">
      <p>
        The edge device is reporting a deployment failure. The IoT Hub <code>$edgeAgent</code> reported
        <code>lastDesiredStatus.code != 200</code>, meaning the deployment was not successfully applied.
      </p>
      <p>Check the device logs or Azure IoT Hub device twin for more details about what went wrong.</p>
    </div>

    <div *ngIf="normalizedStatus === 'mismatch'" class="health-detail">
      <p>The deployment reports success, but not all expected containers are running on the device.</p>
      <div *ngIf="siteReleases.missingContainers?.length > 0">
        <p><strong>Missing containers:</strong></p>
        <table class="missing-containers-table">
          <thead>
            <tr>
              <th>Container Name</th>
              <th>Expected Version</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of siteReleases.missingContainers">
              <td>{{ c.containerName }}</td>
              <td>
                <code>{{ c.containerVersion }}</code>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p *ngIf="!siteReleases.missingContainers?.length">No specific container details available.</p>
    </div>

    <div *ngIf="normalizedStatus === 'unknown'" class="health-detail">
      <p>
        Deployment health has not been evaluated yet. This may mean the site has no edge device connected, or the
        background health check has not run yet.
      </p>
    </div>
  </div>

  <!-- On-Prem Release Status -->
  <div class="deployment-health-section on-prem-section" *ngIf="siteReleases" [ngClass]="onPremStatusClass">
    <div class="health-header">
      <mat-icon class="health-icon">{{ onPremStatusIcon }}</mat-icon>
      <span class="health-title">On-Prem Reported Versions: {{ onPremStatusLabel }}</span>
      <span *ngIf="siteReleases.reportedContainersAt" class="health-checked-at">
        Last reported {{ siteReleases.reportedContainersAt | date: 'dd.MM.yyyy HH:mm' }} UTC
      </span>
      <span *ngIf="isOnPremStale" class="stale-warning">
        <mat-icon class="stale-icon">schedule</mat-icon> Outdated (no report in 1+ hour)
      </span>
    </div>

    <div *ngIf="onPremStatus === 'never-reported'" class="health-detail">
      <p>The on-prem gateway has never reported container status for this site.</p>
    </div>

    <div *ngIf="onPremStatus === 'mismatch'" class="health-detail">
      <p>The on-prem gateway reports that not all expected containers are running.</p>
      <div *ngIf="siteReleases.reportedMissingContainers?.length > 0">
        <p><strong>Missing containers:</strong></p>
        <table class="missing-containers-table">
          <thead>
            <tr>
              <th>Container Name</th>
              <th>Expected Version</th>
              <th>Registry</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let c of siteReleases.reportedMissingContainers">
              <td>{{ c.containerName }}</td>
              <td>
                <code>{{ c.containerVersion }}</code>
              </td>
              <td>{{ c.containerRegistry || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- Set Desired + Available side by side -->
  <div class="release-form-row">
    <div class="release-form-col">
      <h3>Set Desired Release</h3>
      <mat-form-field style="width: 100%">
        <mat-label>Desired Release</mat-label>
        <mat-select [(ngModel)]="selectedDesiredReleaseId">
          <mat-option [value]="null">-- None (clear) --</mat-option>
          <mat-option *ngFor="let release of allReleases" [value]="release.id">
            {{ getReleaseLabel(release) }}
            <span *ngIf="isCurrentRelease(release.id)"> [CURRENT]</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-raised-button color="primary" (click)="saveDesiredRelease()" [disabled]="saving">
        Save Desired Release
      </button>
    </div>

    <div class="release-form-col">
      <h3>Available Releases (for on-prem sync)</h3>
      <p class="available-hint" *ngIf="siteReleases?.availableReleases?.length > 0">
        Currently: up to <strong>{{ currentNewestAvailableVersion }}</strong> ({{
          siteReleases.availableReleases.length
        }}
        release{{ siteReleases.availableReleases.length === 1 ? '' : 's' }})
      </p>
      <p class="available-hint" *ngIf="!(siteReleases?.availableReleases?.length > 0)">
        No releases currently set as available.
      </p>
      <button mat-raised-button color="primary" (click)="openAvailableReleasesDialog()">Set Available Releases</button>
    </div>
  </div>

  <!-- Container Version Comparison Table -->
  <h3>Container Version Status</h3>
  <div class="release-meta" *ngIf="siteReleases">
    <span class="release-meta-item">
      <span class="release-meta-label">Release</span>
      <span *ngIf="siteReleases.desiredRelease">
        {{ siteReleases.desiredRelease.version }}
      </span>
      <span *ngIf="!siteReleases.desiredRelease" class="release-badge none">Not set</span>
    </span>
    <span class="release-meta-item">
      <span class="release-meta-label">Release trigger</span>
      <span *ngIf="releaseTriggerLabel" class="release-badge" [ngClass]="releaseTriggerBadgeClass">
        {{ releaseTriggerLabel }}
      </span>
      <span *ngIf="!releaseTriggerLabel" class="release-badge none">Never released</span>
    </span>
    <span class="release-meta-item" *ngIf="siteReleases.releasedAt">
      <mat-icon class="release-meta-icon">schedule</mat-icon>
      <span class="release-meta-label">Deployed</span> 
      {{ siteReleases.releasedAt | date: 'dd.MM.yyyy HH:mm':'UTC' }} UTC
    </span>
  </div>
  <table class="container-comparison-table" *ngIf="containerComparisonRows.length > 0">
    <thead>
      <tr>
        <th>Container Name</th>
        <th>Desired Version</th>
        <th>
          On-Prem Reported
          <span *ngIf="siteReleases?.reportedContainersAt" class="col-timestamp">
            ({{ siteReleases.reportedContainersAt | date: 'HH:mm, dd.MM' }} UTC)
          </span>
        </th>
        <th>
          IoT Edge Reported
          <span *ngIf="siteReleases?.deploymentHealthCheckedAt" class="col-timestamp">
            ({{ siteReleases.deploymentHealthCheckedAt | date: 'HH:mm, dd.MM' }} UTC)
          </span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let row of containerComparisonRows">
        <td>{{ row.containerName }}</td>
        <td>{{ row.desiredVersion || '—' }}</td>
        <td
          [class.version-mismatch]="row.onPremMismatch"
          [class.version-outdated]="isOnPremStale && siteReleases?.reportedContainers != null && !row.onPremMismatch"
        >
          <span *ngIf="siteReleases?.reportedContainers == null" class="outdated-label">Never reported</span>
          <span *ngIf="siteReleases?.reportedContainers != null">{{ row.onPremVersion || '—' }}</span>
        </td>
        <td
          [class.version-mismatch]="row.iotEdgeMismatch"
          [class.version-outdated]="isIotEdgeStale && !row.iotEdgeMismatch"
        >
          {{ row.iotEdgeVersion || '—' }}
        </td>
      </tr>
    </tbody>
  </table>
  <p *ngIf="containerComparisonRows.length === 0 && siteReleases" class="no-data-hint">
    No container data available. Set a desired release and wait for reports.
  </p>

  <!-- Available Releases Table -->
  <h3>Available Releases</h3>
  <table mat-table [dataSource]="siteAvailableReleases" class="releases-table">
    <ng-container matColumnDef="version">
      <th mat-header-cell *matHeaderCellDef>Version</th>
      <td mat-cell *matCellDef="let release">
        {{ release.version }}
        <mat-icon *ngIf="isCurrentRelease(release.id)" matTooltip="Currently running" class="status-icon current-icon"
          >check_circle</mat-icon
        >
        <mat-icon *ngIf="isDesiredRelease(release.id)" matTooltip="Desired release" class="status-icon desired-icon"
          >flag</mat-icon
        >
        <mat-icon
          *ngIf="
            release.id === latestRelease?.id && !isDesiredRelease(release.id) && isReleaseAvailableForSite(release.id)
          "
          class="status-icon ready-icon"
          [class.ready-icon-ready]="isReleaseReadyForSite(release)"
          [class.ready-icon-pending]="!isReleaseReadyForSite(release)"
          [matTooltip]="
            isReleaseReadyForSite(release) ? 'Downloaded & ready to install' : 'Available but not yet downloaded'
          "
          >download</mat-icon
        >
      </td>
    </ng-container>

    <ng-container matColumnDef="containers">
      <th mat-header-cell *matHeaderCellDef>Containers</th>
      <td mat-cell *matCellDef="let release; let i = index">
        <span
          *ngFor="let c of release.containers; let last = last"
          [class.container-changed]="isContainerChanged(i, c)"
        >
          {{ c.containerName }}:{{ c.containerVersion }}<span *ngIf="!last">, </span>
        </span>
      </td>
    </ng-container>

    <ng-container matColumnDef="releaseDate">
      <th mat-header-cell *matHeaderCellDef>Release created</th>
      <td mat-cell *matCellDef="let release">
        <span *ngIf="release.releaseDate">{{ release.releaseDate | date: 'dd.MM.yyyy' }}</span>
        <span *ngIf="!release.releaseDate" class="no-changelog">—</span>
      </td>
    </ng-container>

    <ng-container matColumnDef="changelog">
      <th mat-header-cell *matHeaderCellDef>Changelog</th>
      <td mat-cell *matCellDef="let release">
        <button
          mat-icon-button
          (click)="toggleChangelog(release.id)"
          *ngIf="release.changelog"
          [matTooltip]="release.changelog.title"
        >
          <mat-icon>description</mat-icon>
        </button>
        <span *ngIf="!release.changelog" class="no-changelog">—</span>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="['version', 'releaseDate', 'containers', 'changelog']; sticky: true"></tr>
    <tr mat-row *matRowDef="let row; columns: ['version', 'releaseDate', 'containers', 'changelog']"></tr>
  </table>

  <!-- Expanded Changelog -->
  <mat-card appearance="outlined" class="changelog-card" *ngIf="expandedChangelogRelease?.changelog as cl">
    <mat-card-subtitle>
      Changelog — {{ cl.title }}
      <span *ngIf="cl.releaseVersion" class="changelog-version">(v{{ cl.releaseVersion }})</span>
    </mat-card-subtitle>
    <mat-card-content>
      <p *ngIf="cl.heading && cl.heading !== cl.title" class="changelog-heading">{{ cl.heading }}</p>
      <img
        *ngIf="getChangelogImageUrl(cl) as imgUrl"
        [src]="imgUrl"
        alt="Changelog image"
        class="changelog-image"
      />
      <div class="changelog-content" [innerHTML]="getSafeHtml(cl.htmlContent)"></div>
      <p *ngIf="cl.linkToManual" class="changelog-manual-link">
        <a [href]="cl.linkToManual" target="_blank" rel="noopener noreferrer">
          <mat-icon class="inline-link-icon">open_in_new</mat-icon> More info
        </a>
      </p>
    </mat-card-content>
  </mat-card>
</mat-card>
